import { useCallback, useEffect, useRef, useState } from "react";
import { HOMEWORK_PHOTO_MAX_BYTES, HOMEWORK_PHOTO_MAX_EDGE } from "@edu/shared";
import * as api from "../../../lib/api.ts";
import { encodeBoardImage } from "../../board/board-image-encode.ts";
import { newId } from "./new-id.ts";
import {
  addFiles,
  addMediaFile,
  overflowOf,
  materialUploadErrorText,
  readyItem,
  roomLeft,
  toggleFile,
  type MaterialItem,
  type MaterialThumb,
} from "./material.ts";

/**
 * Состояние блока «Материал» формы выдачи: текст задания и картинки (1.1-1.2
 * `docs/homework-scratch-design.md`).
 *
 * ⚠️ **Загрузка начинается сразу при выборе**, а не на «Задать» (1.2): при
 * 128 кбит/с картинка в 800 КБ идёт почти минуту, а объекты `File` не
 * переживают выгрузку вкладки. Цена названа в проекте: брошенная форма
 * оставляет файлы в библиотеке, в папке «Материал к домашкам».
 *
 * ⚠️ Модуль лёгкий намеренно: форма выдачи грузится статически (`App.tsx`),
 * поэтому здесь ни одного значения тяжелее ужатия картинки.
 */

export interface MaterialState {
  taskText: string;
  setTaskText: (text: string) => void;
  items: readonly MaterialItem[];
  /** Выбранные на устройстве файлы. Не вошедшие в потолок называются вслух (`overflow`). */
  addDeviceFiles: (files: readonly File[]) => void;
  /** Сколько файлов из последнего выбора не вошло в потолок. */
  overflow: number;
  retry: (key: string) => void;
  remove: (key: string) => void;
  toggle: (fileId: string, thumb: MaterialThumb) => void;
  addAll: (files: readonly { fileId: string; thumb: MaterialThumb }[]) => void;
  /** Файл из Материалов: картинка, аудио или видео в конец списка (Р3 плана медиа). */
  addMedia: (fileId: string, thumb: MaterialThumb) => void;
  /** Продолжение раздачи и «Задать ещё раз»: текст и готовые файлы разом. */
  restore: (taskText: string, files: readonly { fileId: string; thumb: MaterialThumb }[]) => void;
}

function codeOf(err: unknown): string {
  if (err instanceof api.ApiError) return err.code;
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : "";
}

function revokeThumb(item: MaterialItem | undefined): void {
  if (item?.thumb.kind === "blob") URL.revokeObjectURL(item.thumb.url);
}

export function useMaterial(): MaterialState {
  const [taskText, setTaskText] = useState("");
  const [items, setItems] = useState<readonly MaterialItem[]>([]);
  const [overflow, setOverflow] = useState(0);
  /** Исходные файлы по ключу строки: без них повторить упавшую загрузку нечем. */
  const sources = useRef(new Map<string, File>());
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const patch = useCallback((key: string, fn: (it: MaterialItem) => MaterialItem): void => {
    setItems((prev) => prev.map((it) => (it.key === key ? fn(it) : it)));
  }, []);

  /** Живые отправки по ключу строки: «Убрать» и уход с формы их обрывают. */
  const controllers = useRef(new Map<string, AbortController>());
  const alive = (key: string, signal: AbortSignal): boolean =>
    !signal.aborted && itemsRef.current.some((it) => it.key === key);

  const upload = useCallback(
    async (key: string, file: File): Promise<void> => {
      controllers.current.get(key)?.abort();
      const controller = new AbortController();
      controllers.current.set(key, controller);
      const { signal } = controller;
      patch(key, (it) => ({ ...it, status: "uploading", progress: 0, error: null }));
      try {
        const encoded = await encodeBoardImage(file, {
          maxEdge: HOMEWORK_PHOTO_MAX_EDGE,
          maxBytes: HOMEWORK_PHOTO_MAX_BYTES,
          lossy: true,
        });
        // Строку убрали, пока ужимали: отправлять и рисовать некому
        if (!alive(key, signal)) return;
        // Миниатюра из ужатых байтов, а не из исходника: фото с телефона в
        // 5 МБ декодировать ради квадрата в пять сантиметров незачем
        const url = URL.createObjectURL(encoded.blob);
        patch(key, (it) => {
          revokeThumb(it);
          return { ...it, thumb: { kind: "blob", url } };
        });
        // Строки нет и после записи: адрес освобождаем сразу, иначе он живёт до
        // закрытия вкладки
        if (!alive(key, signal)) {
          URL.revokeObjectURL(url);
          return;
        }
        const res = await api.uploadHomeworkMaterialImage(
          encoded.blob,
          { width: encoded.width, height: encoded.height },
          {
            onProgress: (fraction) => patch(key, (it) => ({ ...it, progress: fraction })),
            signal,
          },
        );
        sources.current.delete(key);
        patch(key, (it) => ({ ...it, fileId: res.file.id, status: "ready", progress: 1 }));
      } catch (e) {
        // Отменённая отправка это «Убрать» или уход с формы, а не отказ
        if (signal.aborted) return;
        patch(key, (it) => ({
          ...it,
          status: "failed",
          error: materialUploadErrorText(codeOf(e)),
        }));
      } finally {
        if (controllers.current.get(key) === controller) controllers.current.delete(key);
      }
    },
    [patch],
  );

  /**
   * ⚠️ Стабильная ссылка обязательна: «Задать ещё раз» зовёт восстановление из
   * эффекта, и новая функция на каждый рендер крутила бы его без конца.
   */
  const restore = useCallback(
    (text: string, files: readonly { fileId: string; thumb: MaterialThumb }[]): void => {
      setTaskText(text);
      setOverflow(0);
      sources.current.clear();
      setItems((prev) => {
        for (const it of prev) revokeThumb(it);
        return files.map((f) => readyItem(f.fileId, f.thumb));
      });
    },
    [],
  );

  // Адреса миниатюр освобождаются вместе с формой
  useEffect(
    () => () => {
      for (const c of controllers.current.values()) c.abort();
      for (const it of itemsRef.current) revokeThumb(it);
    },
    [],
  );

  return {
    taskText,
    setTaskText,
    items,
    overflow,
    addDeviceFiles: (files) => {
      const room = roomLeft(itemsRef.current);
      const taken = files.slice(0, room);
      setOverflow(files.length - taken.length);
      const added: MaterialItem[] = taken.map((file) => {
        const key = newId();
        sources.current.set(key, file);
        return { key, fileId: null, status: "uploading", progress: 0, error: null, thumb: { kind: "file" } };
      });
      setItems((prev) => [...prev, ...added]);
      // По одной, а не разом: двадцать параллельных отправок на узком канале
      // это не быстрее, а очередь в одном месте и прогресс, стоящий у всех
      void (async () => {
        for (const it of added) {
          const file = sources.current.get(it.key);
          if (file !== undefined) await upload(it.key, file);
        }
      })();
    },
    retry: (key) => {
      const file = sources.current.get(key);
      if (file !== undefined) void upload(key, file);
    },
    remove: (key) => {
      sources.current.delete(key);
      controllers.current.get(key)?.abort();
      setOverflow(0);
      setItems((prev) => {
        revokeThumb(prev.find((it) => it.key === key));
        return prev.filter((it) => it.key !== key);
      });
    },
    toggle: (fileId, thumb) => {
      setOverflow(0);
      setItems((prev) => toggleFile(prev, fileId, thumb));
    },
    addAll: (files) => {
      // Считаем от того же снимка, что и добавляем: сколько не поместилось,
      // говорится вслух так же, как у загрузки с устройства
      const prev = itemsRef.current;
      const next = addFiles(prev, files);
      setOverflow(overflowOf(prev, files, next));
      setItems(next);
    },
    addMedia: (fileId, thumb) => {
      setOverflow(0);
      setItems((prev) => addMediaFile(prev, fileId, thumb));
    },
    restore,
  };
}
